'use client';

import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
} from '@/components/ui/dialog';
import { XIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useImageNavigation } from '@/providers/image-navigation-provider';

const ImageLightbox: React.FC = () => {
  const {
    currentImage,
    currentIndex,
    totalImages,
    isOpen,
    closeLightbox,
    navigateNext,
    navigatePrevious,
  } = useImageNavigation();

  const [currentImageLoaded, setCurrentImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [displayImage, setDisplayImage] = useState<string | null>(null);
  const [nextImage, setNextImage] = useState<string | null>(null);
  const [nextImageLoaded, setNextImageLoaded] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const currentImageUrl = currentImage?.imageUrl || null;
  const previousImageUrl = useRef<string | null>(null);

  // Handle image changes with cross-fade
  useEffect(() => {
    if (!currentImageUrl) return;

    // If this is the first image or lightbox just opened
    if (!displayImage) {
      setDisplayImage(currentImageUrl);
      setCurrentImageLoaded(false);
      setImageError(false);
      return;
    }

    // If the image actually changed
    if (currentImageUrl !== previousImageUrl.current) {
      setNextImage(currentImageUrl);
      setNextImageLoaded(false);
      setImageError(false);
      setIsTransitioning(true);
    }

    previousImageUrl.current = currentImageUrl;
  }, [currentImageUrl, displayImage]);

  // Handle cross-fade when next image loads
  useEffect(() => {
    if (nextImageLoaded && nextImage && isTransitioning) {
      // Start transition
      const timer = setTimeout(() => {
        setDisplayImage(nextImage);
        setCurrentImageLoaded(true);
        setNextImage(null);
        setNextImageLoaded(false);
        setIsTransitioning(false);
      }, 50); // Brief delay to ensure smooth transition

      return () => clearTimeout(timer);
    }
  }, [nextImageLoaded, nextImage, isTransitioning]);

  // Reset when lightbox closes
  useEffect(() => {
    if (!isOpen) {
      setDisplayImage(null);
      setNextImage(null);
      setCurrentImageLoaded(false);
      setNextImageLoaded(false);
      setIsTransitioning(false);
      setImageError(false);
      previousImageUrl.current = null;
    }
  }, [isOpen]);

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleOverlayClick = () => {
    closeLightbox();
  };

  const handleContextMenu = (e: React.MouseEvent<HTMLImageElement>) => {
    e.preventDefault();
  };

  if (!currentImage) {
    return null;
  }

  const canNavigatePrevious = currentIndex > 0;
  const canNavigateNext = currentIndex < totalImages - 1;
  const showNavigation = totalImages > 1;

  const showSkeleton = !displayImage || (!currentImageLoaded && !isTransitioning);

  return (
    <Dialog open={isOpen} onOpenChange={closeLightbox}>
      <DialogPortal>
        <DialogOverlay 
          className="fixed inset-0 z-50 bg-black/90 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 cursor-zoom-out"
          onClick={handleOverlayClick}
        />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="relative w-[90vw] h-[90vh] max-w-[1200px] max-h-[800px]"
            onClick={handleContentClick}
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 rounded-full bg-secondary backdrop-blur-sm p-2.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-all duration-200 focus:outline-none cursor-pointer shadow-lg hover:shadow-xl hover:scale-110"
              aria-label="Close image"
            >
              <XIcon className="h-5 w-5" />
            </button>

            {/* Image Counter */}
            {showNavigation && (
              <div className="absolute top-4 left-4 z-10 bg-secondary backdrop-blur-sm text-muted-foreground px-3 py-2 rounded-lg text-sm font-medium shadow-lg">
                {currentIndex + 1} / {totalImages}
              </div>
            )}

            {/* Navigation Arrows */}
            {showNavigation && (
              <>
                <button
                  onClick={navigatePrevious}
                  disabled={!canNavigatePrevious}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-10 rounded-full bg-secondary backdrop-blur-sm p-3 text-muted-foreground hover:bg-accent hover:text-foreground transition-all duration-200 focus:outline-none disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:bg-secondary cursor-pointer shadow-lg hover:shadow-xl hover:scale-110"
                  aria-label="Previous image"
                >
                  <ChevronLeftIcon className="h-6 w-6" />
                </button>

                <button
                  onClick={navigateNext}
                  disabled={!canNavigateNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-10 rounded-full bg-secondary backdrop-blur-sm p-3 text-muted-foreground hover:bg-accent hover:text-foreground transition-all duration-200 focus:outline-none disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:bg-secondary cursor-pointer shadow-lg hover:shadow-xl hover:scale-110"
                  aria-label="Next image"
                >
                  <ChevronRightIcon className="h-6 w-6" />
                </button>
              </>
            )}

            {/* Image Container */}
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Loading Skeleton */}
              {showSkeleton && (
                <Skeleton className="absolute inset-0 w-full h-full bg-white/20 rounded-sm" />
              )}
              
              {/* Current/Display Image */}
              {displayImage && !imageError && (
                <img
                  src={displayImage}
                  alt={currentImage.alt}
                  title={currentImage.title}
                  className="absolute inset-0 w-full h-full object-contain rounded-sm select-none transition-opacity duration-300 cursor-default"
                  style={{ 
                    opacity: currentImageLoaded ? 1 : 0
                  }}
                  onLoad={() => setCurrentImageLoaded(true)}
                  onError={() => {
                    setImageError(true);
                    setCurrentImageLoaded(false);
                  }}
                  draggable={false}
                  onContextMenu={handleContextMenu}
                />
              )}

              {/* Next Image (preloading for cross-fade) */}
              {nextImage && (
                <img
                  src={nextImage}
                  alt={currentImage.alt}
                  className="absolute inset-0 w-full h-full object-contain rounded-sm select-none opacity-0 pointer-events-none"
                  onLoad={() => setNextImageLoaded(true)}
                  onError={() => {
                    setImageError(true);
                    setIsTransitioning(false);
                  }}
                  draggable={false}
                />
              )}

              {/* Error State */}
              {imageError && (
                <div className="absolute inset-0 w-full h-full flex items-center justify-center">
                  <div className="text-center bg-white/10 backdrop-blur-sm rounded-lg p-8 shadow-lg">
                    <div className="text-white/70 text-lg mb-2">⚠️</div>
                    <span className="text-white/90 text-sm">Failed to load image</span>
                  </div>
                </div>
              )}

              {/* Image title overlay */}
              {currentImage.title && currentImageLoaded && !imageError && (
                <div className="absolute bottom-4 left-4 bg-secondary backdrop-blur-sm text-muted-foreground px-4 py-3 rounded-lg text-sm max-w-2xl shadow-lg">
                  <p className="font-medium">{currentImage.title}</p>
                </div>
              )}
            </div>

            {/* Navigation hint */}
            {showNavigation && currentImageLoaded && !imageError && (
              <div className="absolute bottom-4 right-4 text-muted-foreground/80 text-xs bg-secondary/80 backdrop-blur-sm px-3 py-2 rounded-lg font-medium shadow-lg">
                Use ← → keys to navigate
              </div>
            )}
          </div>
        </div>
      </DialogPortal>
    </Dialog>
  );
};

export default ImageLightbox; 